import { useContext } from "react";
import { CachedTokenContext } from "@seabrookstudios/auth";
import { useMyEntitlements } from "@seabrookstudios/pitch2table";
import { s2Url } from "@seabrookstudios/types";
import endpoints from "../endpoints";
import { assetPath } from "../endpoints";
import {
  isFileTooLargeForStandardImportUpload as isMediaFileTooLargeForStandardUpload,
  shouldSkipImportUpload as shouldSkipMediaImport,
  STANDARD_IMPORT_UPLOAD_LIMIT_BYTES as STANDARD_MEDIA_UPLOAD_LIMIT_BYTES,
} from "../nestifyz/importUploadUtils";
import { usePreSignedUploadImage, useUploadImage } from "../users/queries";

export { getImportUploadSkipMessage as getMediaImportSkipMessage } from "../nestifyz/importUploadUtils";
export { isMediaFileTooLargeForStandardUpload, shouldSkipMediaImport, STANDARD_MEDIA_UPLOAD_LIMIT_BYTES };

/**
 * @param {string|object} response
 * @returns {string|undefined}
 */
export const parseUrlFromUploadResponse = (response) => {
  if (typeof response === "object" && response !== null) {
    return "url" in response ? response.url : undefined;
  }

  if (typeof response !== "string" || !response) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(response);

    if (typeof parsed === "object" && parsed !== null) {
      return parsed.url;
    }

    return typeof parsed === "string" ? parsed : undefined;
  } catch {
    return response;
  }
};

/**
 * @param {import("@seabrookstudios/pitch2table-core").GameAsset[]} files
 * @param {string} uploadedUrl
 * @returns {import("@seabrookstudios/pitch2table-core").GameAsset|undefined}
 */
export const findGameAssetByUrl = (files, uploadedUrl) => {
  return files.find((file) => {
    if (!file.s3location) {
      return false;
    }

    if (uploadedUrl === file.s3location) {
      return true;
    }

    if (uploadedUrl === assetPath(file.s3location)) {
      return true;
    }

    return uploadedUrl.endsWith(`/${file.s3location}`);
  });
};

/**
 * @param {string|undefined} location
 * @returns {string|undefined}
 */
const getLocationFilename = (location) => {
  if (!location) {
    return undefined;
  }

  return location.split("/").pop()?.split("?")[0];
};

/**
 * @param {{name?: string, s3location?: string, bytes?: number, mimeType?: string, deleted?: boolean}} asset
 * @param {File} file
 * @returns {boolean}
 */
export const mediaMatchesImportFile = (asset, file) => {
  if (asset.deleted) {
    return false;
  }

  if (asset.name === file.name) {
    return true;
  }

  const locationFilename = getLocationFilename(asset.s3location);

  if (locationFilename && locationFilename === file.name) {
    return true;
  }

  if (asset.bytes === file.size && asset.mimeType === file.type) {
    return true;
  }

  return false;
};

/**
 * @param {import("@seabrookstudios/pitch2table-core").GameAsset[]} files
 * @param {File} file
 * @returns {boolean}
 */
export const hasExistingImportedGameMedia = (files, file) => {
  return files.some((asset) => mediaMatchesImportFile(asset, file));
};

/**
 * @param {import("@seabrookstudios/types").GameId} gameId
 * @param {(url: string) => void} onSuccess
 * @param {() => void} onError
 */
export const useUploadGameAsset = (gameId, onSuccess, onError) => {
  const token = useContext(CachedTokenContext);
  const { data: entitlements } = useMyEntitlements();

  const onUploadSuccess = (response) => {
    const url = parseUrlFromUploadResponse(response);

    if (!url) {
      onError();
      return;
    }

    onSuccess(url);
  };

  const { games } = endpoints();
  const standardUpload = useUploadImage(s2Url(games().asset(gameId)), token, "asset", onUploadSuccess, onError);
  const premiumUpload = usePreSignedUploadImage(
    games().assetStart(gameId),
    games().assetFinish(gameId),
    token,
    onUploadSuccess,
    onError
  );

  return entitlements?.hasLargeFileUploads ? premiumUpload : standardUpload;
};
